<template>
  <Transition name="fade">
    <div
      v-if="showOffcanvas"
      class="rbz-offcanvas-wrapper"
      role="dialog"
      ref="offcanvasRef"
      aria-modal="true"
      aria-labelledby="offcanvas-header-title"
    >
      <div class="rbz-offcanvas-header">
        <div class="rbz-offcanvas-header-title">
          <slot name="header" />
        </div>
        <rbz-form-button
          class="close-offcanvas-button"
          variant="tertiary"
          :ignore-update-in-process="true"
          @click="closeOffcanvas"
          icon="IconClose"
          size="small"
          data-qa="close-offcanvas-button"
        />
      </div>
      <div class="rbz-offcanvas-content">
        <slot name="content" />
      </div>
      <div class="rbz-offcanvas-footer">
        <slot name="footer" />
      </div>
    </div>
  </Transition>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import RbzFormButton from '@/components/RbzFormButton.vue';

export default defineComponent({
  name: 'RbzOffcanvas',
  components: {
    RbzFormButton,
  },
  props: {
    showOffcanvas: {
      type: Boolean,
      required: true,
      default: false,
    },
  },
  data() {
    return {
      offcanvasRef: null,
    };
  },
  watch: {
    showOffcanvas(newVal) {
      if (newVal) {
        this.addEventListeners();
      } else {
        this.removeEventListeners();
      }
    },
  },
  methods: {
    closeOffcanvas() {
      this.$emit('update:showOffcanvas', false);
    },
    addEventListeners() {
      document.addEventListener('mousedown', this.handleOutsideClick);
      document.addEventListener('keydown', this.handleKeydown);
    },
    removeEventListeners() {
      document.removeEventListener('mousedown', this.handleOutsideClick);
      document.removeEventListener('keydown', this.handleKeydown);
    },
    handleOutsideClick(event: MouseEvent) {
      if (this.$refs.offcanvasRef && !this.$refs.offcanvasRef.contains(event.target)) {
        this.closeOffcanvas();
      }
    },
    handleKeydown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        this.closeOffcanvas();
      }
    },
  },
  mounted() {
    if (this.showOffcanvas) {
      this.addEventListeners();
    }
  },
  beforeUnmount() {
    this.removeEventListeners();
  },
});
</script>

<style scoped lang="scss">
@use 'sass:map';
@use 'ui-component-library/assets/styles/colors' as colors;

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.rbz-offcanvas-wrapper {
  position: fixed;
  right: 0;
  top: 56px;
  height: calc(100% - 56px);
  background-color: map.get(colors.$colors, 'alabaster');
  z-index: 99999;
  width: 600px;
  padding: 50px 32px 24px;
}

.rbz-offcanvas-header {
  display: flex;
  justify-content: space-between;
  width: 100%;
  height: 36px;
  padding: 5px 0;

  .rbz-button {
    background: none;
  }

  .rbz-offcanvas-header-title {
    color: map.get(colors.$colors, 'black');
    font-size: 20px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
  }
}

:deep(.close-offcanvas-button .rbz-button) {
  border: 0;
  height: 24px;
  padding: 0;
  width: 24px;
  background-color: map.get(colors.$colors, 'alabaster');

  &.icon-only {
    padding: 0;
  }

  &:hover {
    background-color: map.get(colors.$colors, 'jewel');
    color: map.get(colors.$colors, 'white');
  }
}

.rbz-offcanvas-content {
  border-radius: 4px;
  padding: 16px;
  gap: 40px;
  height: calc(100% - 86px);
  overflow-y: auto;
  background-color: map.get(colors.$colors, 'white');
}

.rbz-offcanvas-footer {
  display: flex;
  justify-content: flex-end;
}
</style>
